/**
 * WallpaperExploreGrid — flat, fast grid for the Explore → Wallpapers tab.
 *
 * Rebuilt from scratch (see WallpaperExploreCard.tsx for the per-card rebuild
 * notes). Root cause of the old freeze: entering the Explore tab re-mounted a
 * whole tree of heavy WallpaperCard instances at once — each one carrying two
 * always-mounted Radix edit/delete dialogs, a long-press-preview overlay, a
 * duplicate `new Image()` preloader on top of the real <img>, and its own
 * @tanstack/react-virtual windowed-virtualizer instance (two ResizeObservers)
 * — regardless of admin status or whether those dialogs were ever opened.
 *
 * This version:
 *   - Plain CSS grid, fixed column count → no gaps, no measuring.
 *   - No JS virtualization. Cards render directly; `content-visibility: auto`
 *     on each card (see WallpaperExploreCard) lets the browser skip
 *     layout/paint for off-screen cards instead of a windowing library.
 *   - Renders in capped chunks (progressive reveal via IntersectionObserver)
 *     so a catalog of hundreds of items doesn't mount all at once — this is
 *     the "simplest correct" alternative to full virtualization.
 *   - Admin edit/delete is delegated to the shared <AdminEditableCard>, which
 *     is a complete no-op for non-admins (zero dialogs mounted).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ImageIcon } from 'lucide-react';
import { WallpaperExploreCard } from '@/components/WallpaperExploreCard';
import { AdminEditableCard } from '@/components/AdminEditableCard';
import type { WallpapersResponse } from '@/lib/collections/wallpapers';

const CHUNK_SIZE = 12;
const COL_GAP = 8;
// Rows deliberately overlap by ~4px, matching the live grid: there the virtualized
// row height is computed from a column width that is 8px too narrow, which at the
// 9/14 card aspect under-measures each row by ~12px against an 8px gap.
const ROW_OVERLAP = 4;
const PADDING_H = 8;

interface WallpaperExploreGridProps {
  wallpapers: WallpapersResponse[];
  purchasedIds: Set<string>;
  onSelectWallpaper: (w: WallpapersResponse, fromLibrary: boolean) => void;
  gridColumns?: number;
  emptyMessage?: string;
  emptyAction?: { label: string; onClick: () => void };
}

export const WallpaperExploreGrid: React.FC<WallpaperExploreGridProps> = ({
  wallpapers,
  purchasedIds,
  onSelectWallpaper,
  gridColumns = 2,
  emptyMessage = 'No wallpapers yet.',
  emptyAction,
}) => {
  const cols = Math.max(1, gridColumns);
  const [visibleCount, setVisibleCount] = useState(CHUNK_SIZE);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Reset the reveal window whenever the underlying list actually changes in
  // *content* (new filter/category/search), not merely a new array reference.
  // Realtime subscriptions emit a fresh array reference on any unrelated
  // write (e.g. another item's purchase count ticking up) even when the
  // resulting id set is identical — keying off reference identity would
  // collapse the already-revealed window back to CHUNK_SIZE items right as
  // the user closes a full-view overlay elsewhere, which shrinks the page
  // height and clamps the restored scroll position back near the top.
  const contentSignature = useMemo(() => wallpapers.map((w) => w.id).join(','), [wallpapers]);
  useEffect(() => {
    setVisibleCount(CHUNK_SIZE);
  }, [contentSignature]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setVisibleCount((c) => Math.min(c + CHUNK_SIZE, wallpapers.length));
      }
    }, { rootMargin: '600px 0px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [wallpapers.length]);

  const visibleWallpapers = useMemo(
    () => wallpapers.slice(0, visibleCount),
    [wallpapers, visibleCount],
  );

  if (wallpapers.length === 0) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: '80px 24px', gap: 16, textAlign: 'center',
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%',
          background: 'rgba(139,92,246,0.12)',
          border: '1px solid rgba(139,92,246,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <ImageIcon size={28} color="#8b5cf6" />
        </div>
        <p style={{ color: 'rgba(240,240,245,0.45)', fontSize: 15, margin: 0, maxWidth: 280 }}>
          {emptyMessage}
        </p>
        {emptyAction && (
          <button
            onClick={emptyAction.onClick}
            style={{
              padding: '10px 22px', borderRadius: 10,
              background: 'rgba(139,92,246,0.8)',
              border: 'none', color: '#fff',
              cursor: 'pointer', fontSize: 14, fontWeight: 700,
              fontFamily: "'Syne', sans-serif",
            }}
          >
            {emptyAction.label}
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: `0 ${PADDING_H}px` }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          columnGap: COL_GAP,
          rowGap: 0,
        }}
      >
        {visibleWallpapers.map((w, idx) => (
          <div
            key={w.id}
            style={{ marginTop: idx < cols ? 0 : -ROW_OVERLAP }}
          >
            <AdminEditableCard item={w}>
              <WallpaperExploreCard
                wallpaper={w}
                isPurchased={purchasedIds.has(w.id)}
                onClick={() => onSelectWallpaper(w, false)}
              />
            </AdminEditableCard>
          </div>
        ))}
      </div>
      {/* Sentinel — growing the reveal window as the user scrolls near the end */}
      {visibleCount < wallpapers.length && (
        <div ref={sentinelRef} style={{ height: 1 }} />
      )}
    </div>
  );
};

export default WallpaperExploreGrid;
